import { Box, Button, Link, Typography, useMediaQuery } from "@mui/material";
import { styled, useTheme } from "@mui/system";
import { useRouter } from "next/router";
import React from "react";

import BeegruPremium from "../../../../public/images/illustrations/Intro/agent/beegru_premium.svg";
import Boost from "../../../../public/images/illustrations/Intro/agent/boost.svg";
import Dashboard from "../../../../public/images/illustrations/Intro/agent/dashboard.svg";
import People from "../../../../public/images/illustrations/Intro/agent/people.svg";
import ListCards from "../../../cards/introListCard";

/* Styled Components */

const HeroSection = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
	background: "#FEF2E9",
	minHeight: "300px",
	padding: "3rem 1.5rem",
	textAlign: "center",
	[theme.breakpoints.down("sm")]: {
		padding: "2rem 1.5rem",
		minHeight: "150px",
	},
}));

const Heading = styled(Typography)(({ theme }) => ({
	fontSize: "60px",
	fontWeight: "300",
	lineHeight: "72px",
	color: "#000000",
	margin: "0rem 0rem 1rem 0rem",
	[theme.breakpoints.down("md")]: {
		fontSize: "48px",
		lineHeight: "58px",
	},
	[theme.breakpoints.down("sm")]: {
		fontSize: "24px",
		lineHeight: "29px",
	},
}));

const SubText = styled(Typography)(({ theme }) => ({
	fontSize: "1.5rem",
	fontWeight: "400",
	color: "#666666",
	maxWidth: "none",
	margin: "0 auto",
	whiteSpace: "nowrap",
	textAlign: "center",
	[theme.breakpoints.down("md")]: {
		fontSize: "20px",
		whiteSpace: "normal",
		maxWidth: "800px",
	},
	[theme.breakpoints.down("sm")]: {
		fontSize: "12px",
		whiteSpace: "normal",
		maxWidth: "100%",
	},
}));

const CardsContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	width: "100%",
	gap: "1rem",
	padding: "2rem 12rem 2rem 12rem",
	[theme.breakpoints.down("md")]: {
		padding: "2rem 1rem",
	},
}));

const CardRow = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	gap: "2rem",
	[theme.breakpoints.down("sm")]: {
		flexDirection: "column",
	},
	[theme.breakpoints.up("lg")]: {
		"& :hover": {
			cursor: "pointer",
			transform: "scale(1.02)",
			transition: "transform 0.2s ease-in-out",
		},
	},
}));

const AdditionalCardsRow = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	gap: "2rem",
	width: "100%",
	marginTop: "2rem",
	[theme.breakpoints.down("md")]: {
		flexDirection: "column",
	},
}));

const AiBoostCard = styled(Link)(({ theme }) => ({
	position: "relative",
	backgroundImage: "url(/images/icons/ai-boost/rocket-background1.jpg)",
	backgroundSize: "cover",
	backgroundPosition: "center",
	backgroundRepeat: "no-repeat",
	borderRadius: "12px",
	border: "1px solid #291C78",
	padding: "2rem",
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	justifyContent: "space-between",
	textDecoration: "none",
	color: "inherit",
	overflow: "hidden",
	flex: 1,
	height: "280px",
	maxHeight: "280px",
	[theme.breakpoints.down("md")]: {
		flexDirection: "row",
		textAlign: "left",
		padding: "1rem",
		height: "250px",
		maxHeight: "250px",
		alignItems: "center",
		gap: "1rem",
		marginBottom: "2rem",
	},
}));

const AiBoostContent = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	gap: "1rem",
	flex: 1,
	zIndex: 1,
	[theme.breakpoints.down("md")]: {
		flex: "1",
		gap: "0.5rem",
	},
}));

const AiBoostTitle = styled(Typography)(({ theme }) => ({
	fontSize: "34px",
	fontWeight: "400",
	color: "#492BBD",
	lineHeight: "1.2",
	[theme.breakpoints.down("md")]: {
		fontSize: "16px",
		lineHeight: "1.3",
	},
}));

const AiBoostDescription = styled(Typography)(({ theme }) => ({
	fontSize: "16px",
	fontWeight: "400",
	color: "#000000",
	lineHeight: "1.5",
	maxWidth: "400px",
	[theme.breakpoints.down("md")]: {
		maxWidth: "100%",
		fontSize: "12px",
		lineHeight: "1.6",
		color: "#666666",
	},
}));

const AiBoostButton = styled(Button)(({ theme }) => ({
	backgroundColor: "#492BBD",
	color: "#FFFFFF",
	padding: "0.75rem 2rem",
	borderRadius: "0.75rem",
	textTransform: "none",
	fontSize: "1rem",
	fontWeight: "500",
	alignSelf: "flex-start",
	boxShadow: "none",
	"&:hover": {
		backgroundColor: "#39169E",
		boxShadow: "none",
	},
	[theme.breakpoints.down("md")]: {
		alignSelf: "flex-start",
		fontSize: "0.75rem",
		padding: "0.5rem 1rem",
	},
}));

const AiBoostImageContainer = styled(Box)(({ theme }) => ({
	position: "relative",
	width: "180px",
	height: "180px",
	flexShrink: 0,
	[theme.breakpoints.down("md")]: {
		position: "relative",
		top: "auto",
		left: "auto",
		transform: "none",
		width: "100px",
		height: "100px",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},
}));

const BenefitsCard = styled(Link)(({ theme }) => ({
	position: "relative",
	backgroundColor: "#FEF2E9",
	borderRadius: "12px",
	border: "1px solid #6E3037",
	padding: "2rem",
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	justifyContent: "space-between",
	textDecoration: "none",
	color: "inherit",
	overflow: "hidden",
	flex: 1,
	height: "280px",
	maxHeight: "280px",
	[theme.breakpoints.down("md")]: {
		flexDirection: "row",
		textAlign: "left",
		padding: "1rem",
		height: "220px",
		maxHeight: "220px",
		alignItems: "center",
		gap: "0.5rem",
	},
}));

const BenefitsCardContent = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	gap: "1rem",
	flex: 1,
}));

const BenefitsCardTitle = styled(Typography)(({ theme }) => ({
	fontSize: "34px",
	fontWeight: "400",
	color: "#212121",
	lineHeight: "1.2",
	[theme.breakpoints.down("md")]: {
		fontSize: "16px",
		lineHeight: "1.3",
	},
}));

const BroadcastButton = styled(Button)(({ theme }) => ({
	backgroundColor: "#621B16",
	color: "#FFFFFF",
	padding: "0.75rem 2rem",
	borderRadius: "0.75rem",
	textTransform: "none",
	fontSize: "1rem",
	fontWeight: "500",
	alignSelf: "flex-start",
	boxShadow: "none",
	"&:hover": {
		backgroundColor: "#4D1511",
		boxShadow: "none",
	},
	[theme.breakpoints.down("md")]: {
		alignSelf: "flex-start",
		fontSize: "0.75rem",
		padding: "0.5rem 1rem",
	},
}));

const AdditionalBenefitsSection = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	gap: "2rem",
	width: "100%",
	padding: "4rem 12rem 4rem 12rem",
	[theme.breakpoints.down("md")]: {
		flexDirection: "column",
		padding: "0rem 1rem 2rem 1rem",
	},
}));

const MobileOnlySection = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	width: "100%",
	padding: "0rem 1rem 2rem 1rem",
}));

const MobileBenefitsDescription = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	gap: "1.5rem",
	alignItems: "flex-start",
	marginBottom: "2rem",
}));

const MobileFAQSection = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	gap: "1.5rem",
	alignItems: "flex-start",
	paddingTop: "2rem",
}));

const BenefitsColumn = styled(Box)(({ theme }) => ({
	flex: 1,
	display: "flex",
	flexDirection: "column",
	gap: "1.5rem",
	alignItems: "flex-start",
}));

const SectionLabel = styled(Typography)(({ theme }) => ({
	fontSize: "1.125rem",
	fontWeight: "500",
	color: "#FC8019",
	letterSpacing: "0.05em",
}));

const SectionHeading = styled(Typography)(({ theme }) => ({
	fontSize: "34px",
	fontWeight: "400",
	color: "#000000",
	lineHeight: "1.2",
	[theme.breakpoints.down("md")]: {
		fontSize: "24px",
	},
}));

const DescriptionText = styled(Typography)(({ theme }) => ({
	fontSize: "16px",
	fontWeight: "400",
	color: "#000000",
	lineHeight: "1.75",
	textAlign: "justify",
	[theme.breakpoints.down("sm")]: {
		fontSize: "12px",
	},
}));

const FAQContainer = styled(Box)(({ theme }) => ({
	backgroundColor: "#FAFAFA",
	borderRadius: "1rem",
	display: "flex",
	flexDirection: "column",
	gap: "1.5rem",
	padding: "0rem 0rem 0rem 0rem",
}));

const AccordionWrapper = styled(Box)(({ theme }) => ({
	borderRadius: "0.5rem",
	border: "0.0625rem solid #C4C4C4",
	overflow: "hidden",
}));

const AccordionItem = styled(Box)(({ theme }) => ({
	borderBottom: "0.0625rem solid #C4C4C4",
	"&:last-child": {
		borderBottom: "none",
	},
}));

const AccordionHeader = styled(Box)<{ isOpen?: boolean }>(({ theme, isOpen }) => ({
	display: "flex",
	justifyContent: "space-between",
	alignItems: "center",
	padding: "1rem 2rem",
	cursor: "pointer",
	backgroundColor: isOpen ? "#F5F5F5" : "#FFFFFF",
	"&:hover": {
		backgroundColor: "#FAFAFA",
	},
	[theme.breakpoints.down("md")]: {
		padding: "0.75rem 1rem",
	},
}));

const AccordionQuestion = styled(Typography)(({ theme }) => ({
	fontSize: "1rem",
	fontWeight: "500",
	color: "#212121",
	[theme.breakpoints.down("sm")]: {
		fontSize: "12px",
	},
}));

const AccordionIcon = styled(Box)(({ theme }) => ({
	width: "1.5rem",
	height: "1.5rem",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	fontSize: "1.5rem",
	color: "#212121",
	fontWeight: "300",
	transition: "transform 0.1875s ease",
}));

const AccordionContent = styled(Box)<{ isOpen?: boolean }>(({ theme, isOpen }) => ({
	padding: "0 2rem 1rem 2rem",
	fontSize: "1rem",
	lineHeight: "1.75",
	color: "#616161",
	backgroundColor: isOpen ? "#F5F5F5" : "#FFFFFF",
	[theme.breakpoints.down("sm")]: {
		fontSize: "12px",
		padding: "0 1rem 0.75rem 1rem",
	},
}));

const AgentNewIntro = () => {
	const router = useRouter();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));
	const [openAccordion, setOpenAccordion] = React.useState<number | null>(0);

	const toggleAccordion = (index: number) => {
		setOpenAccordion(openAccordion === index ? null : index);
	};

	const faqs = [
		{
			question: "How does Beegru help a real estate agent get leads?",
			answer:
				"Use AI Boost for targeted buyer reach on Google & Meta and Broadcast for mandates—everything tracks in an agent-friendly dashboard.",
		},
		{
			question: "Is Beegru useful for a property dealer or property agent?",
			answer:
				"Yes—property dealers and property agents can list inventory, co-broke via Broadcast, and keep all enquiries organized.",
		},
		{
			question: "Can a commercial real estate agent use Beegru?",
			answer:
				"Absolutely. A commercial real estate agent can post spaces, recruit co-brokers, and run AI Boost with micro-market targeting.",
		},
		{
			question: "Who is the best property dealer platform for co-broking?",
			answer:
				"Beegru streamlines co-broking with Broadcast: see mandates, accept proposed commissions, and start selling with clear terms.",
		},
		{
			question: "How does Beegru support real estate agents in Bangalore?",
			answer:
				"Local targeting, Broadcast to agents in Bangalore, and live CPL/CPA help real estate agents in Bangalore scale faster.",
		},
		{
			question: "Do realtors get pre-qualified leads or generic dumps?",
			answer: "Realtors receive pre-qualified leads (junk filtered), with live reporting and channel breakdowns.",
		},
		{
			question: "Can real estate dealers manage team workflows?",
			answer: "Yes—assign leads, set statuses, sort by date/status, add notes, export CSV/PDF.",
		},
		{
			question: "What's included for new property dealers in Bangalore?",
			answer: "Starter plan: up to 5 active listings, Broadcast access, and a unified inbox for leads/conversations.",
		},
	];

	const localLang = {
		broadcastNetwork:
			router.locale === "ar-AE"
				? "الاتصال بالجميع"
				: router.locale === "kn-IN"
					? "ಎಲ್ಲರೊಂದಿಗೂ ಸಂಪರ್ಕಿಸಿ"
					: "Broadcast Network",

		dashboard: router.locale === "ar-AE" ? "لوحة القيادة" : router.locale === "kn-IN" ? "ಡ್ಯಾಶ್ಬೋರ್ಡ್" : "Dashboard",

		aiBoost: router.locale === "ar-AE" ? "ميزة التعزيز" : router.locale === "kn-IN" ? "ಬೂಸ್ಟ್ ವೈಶಿಷ್ಟ್ಯ" : "AI Boost",

		beegruPremium:
			router.locale === "ar-AE" ? "بيجرو بريميوم" : router.locale === "kn-IN" ? "ಬೀಗ್ರೂ ಪ್ರೀಮಿಯಂ" : "Beegru Premium",

		listBroadcastNetwork: [
			router.locale === "ar-AE"
				? "ابحث عن شركاء التعاون والوسطاء المشاركين"
				: router.locale === "kn-IN"
					? "ಸಹ-ಬ್ರೋಕರ್‌ಗಳು ಮತ್ತು ಸಹಯೋಗ ಪಾಲುದಾರರನ್ನು ಹುಡುಕಿ"
					: "Find co-brokers and collaboration partners",
			router.locale === "ar-AE"
				? "اقبل العمولة المشتركة وابدأ البيع بسرعة"
				: router.locale === "kn-IN"
					? "ಹಂಚಿಕೆಯ ಕಮಿಷನ್ ಅನ್ನು ಸ್ವೀಕರಿಸಿ; ತ್ವರಿತವಾಗಿ ಮಾರಾಟ ಪ್ರಾರಂಭಿಸಿ"
					: "Accept shared commission; start selling quickly",
			router.locale === "ar-AE"
				? "تتبع جميع الردود داخل صندوق الوارد الخاص بك"
				: router.locale === "kn-IN"
					? "ನಿಮ್ಮ ಇನ್‌ಬಾಕ್ಸ್‌ನಲ್ಲಿ ಎಲ್ಲಾ ಪ್ರತಿಕ್ರಿಯೆಗಳನ್ನು ಟ್ರ್ಯಾಕ್ ಮಾಡಿ"
					: "Track all replies inside your inbox",
		],

		listDashboard: [
			router.locale === "ar-AE"
				? "عرض واحد للقوائم والعملاء المحتملين و Broadcast و Boost"
				: router.locale === "kn-IN"
					? "ಪಟ್ಟಿಗಳು, ಲೀಡ್‌ಗಳು, Broadcast ಮತ್ತು Boost ಗಾಗಿ ಒಂದು ವೀಕ್ಷಣೆ"
					: "One view for listings, leads, Broadcast, and Boost",
			router.locale === "ar-AE"
				? "تتبع العملاء المحتملين والمشاهدات والحفظ والمشاركات لكل قائمة"
				: router.locale === "kn-IN"
					? "ಪ್ರತಿ ಪಟ್ಟಿಗಾಗಿ ಲೀಡ್‌ಗಳು, ವೀಕ್ಷಣೆಗಳು, ಉಳಿಸಿದವು ಮತ್ತು ಹಂಚಿಕೆಗಳನ್ನು ಟ್ರ್ಯಾಕ್ ಮಾಡಿ"
					: "Track the leads, views, saves & shares for each listing",
			router.locale === "ar-AE"
				? "إدارة العملاء المحتملين بسهولة؛ تعيين الحالات وإضافة الملاحظات والفرز والتصدير"
				: router.locale === "kn-IN"
					? "ನಿಮ್ಮ ಲೀಡ್‌ಗಳನ್ನು ಸುಲಭವಾಗಿ ನಿರ್ವಹಿಸಿ; ಸ್ಥಿತಿಗಳನ್ನು ಹೊಂದಿಸಿ, ಟಿಪ್ಪಣಿಗಳನ್ನು ಸೇರಿಸಿ, ವಿಂಗಡಿಸಿ ಮತ್ತು ರಫ್ತು ಮಾಡಿ"
					: "Manage your leads with ease; set statuses, add notes, sort and export",
		],

		listAiBoost: [
			router.locale === "ar-AE"
				? "إطلاق حملات مدعومة بالذكاء الاصطناعي على Google و Meta ومنصات أخرى"
				: router.locale === "kn-IN"
					? "Google, Meta ಮತ್ತು ಇತರ ಪ್ರಮುಖ ವೇದಿಕೆಗಳಲ್ಲಿ AI-ಚಾಲಿತ ಅಭಿಯಾನಗಳನ್ನು ಪ್ರಾರಂಭಿಸಿ"
					: "Launch AI-powered campaigns on Google, Meta and other top platforms",
			router.locale === "ar-AE"
				? "استهداف بالذكاء الاصطناعي، إنشاء المحتوى الإبداعي، والتحسين المستمر"
				: router.locale === "kn-IN"
					? "AI ಗುರಿ, ಸೃಜನಶೀಲ ಉತ್ಪಾದನೆ ಮತ್ತು ನಿರಂತರ ಆಪ್ಟಿಮೈಸೇಶನ್"
					: "AI targeting, creatives generation, and continuous optimization",
			router.locale === "ar-AE"
				? "عملاء محتملون مؤهلون مسبقاً وتقليل تكاليف التسويق والعمليات بنسبة تصل إلى 70%"
				: router.locale === "kn-IN"
					? "ಪೂರ್ವ-ಅರ್ಹ ಲೀಡ್‌ಗಳು ಮತ್ತು ಮಾರ್ಕೆಟಿಂಗ್ ಮತ್ತು ಕಾರ್ಯಾಚರಣೆ ವೆಚ್ಚವನ್ನು 70% ವರೆಗೆ ಕಡಿಮೆ ಮಾಡಿ"
					: "Pre-qualified leads and cut marketing & operational costs by up to 70%",
			router.locale === "ar-AE"
				? "تتبع المقاييس الرئيسية مثل CPL و CTC والعملاء المؤهلين عبر الحملات"
				: router.locale === "kn-IN"
					? "CPL, CTC, ಅರ್ಹ ಲೀಡ್‌ಗಳಂತಹ ಪ್ರಮುಖ ಮೆಟ್ರಿಕ್ಸ್‌ಗಳನ್ನು ಟ್ರ್ಯಾಕ್ ಮಾಡಿ"
					: "Track key metrics like CPL, CTC, qualified leads across campaigns, view ad previews and a campaign timeline",
		],

		listBeegruPremium: [
			router.locale === "ar-AE"
				? "مجاني لفترة محدودة"
				: router.locale === "kn-IN"
					? "ಸೀಮಿತ ಅವಧಿಗೆ ಉಚಿತ"
					: "Free for limited time",
			router.locale === "ar-AE"
				? "قوائم غير محدودة بالإضافة إلى الأولوية"
				: router.locale === "kn-IN"
					? "ಅನಿಯಮಿತ ಪಟ್ಟಿಗಳು ಮತ್ತು ಆದ್ಯತೆ ನಿಯೋಜನೆ"
					: "Unlimited listings plus priority placement",
			router.locale === "ar-AE"
				? "ادمج مع AI Boost و Broadcast لتوسيع الوصول"
				: router.locale === "kn-IN"
					? "ವ್ಯಾಪ್ತಿ ಹೆಚ್ಚಿಸಲು AI Boost ಮತ್ತು Broadcast ಜೊತೆ ಸಂಯೋಜಿಸಿ"
					: "Pair with AI Boost & Broadcast to scale reach",
		],
	};

	return (
		<>
			<HeroSection>
				<Heading>
					Benefits for <span style={{ color: "#FC8019" }}>Agents</span>
				</Heading>
				<SubText variant="h5">
					List properties, co-broke with Broadcast, market with AI Boost, and manage every lead in one dashboard.
				</SubText>
			</HeroSection>
			<CardsContainer>
				<CardRow>
					<ListCards
						heading={localLang.broadcastNetwork}
						list_text={localLang.listBroadcastNetwork}
						icon={People}
					/>
					<ListCards
						heading={localLang.dashboard}
						list_text={localLang.listDashboard}
						icon={Dashboard}
					/>
				</CardRow>
				<CardRow>
					<ListCards
						heading={localLang.aiBoost}
						list_text={localLang.listAiBoost}
						icon={Boost}
					/>
					<ListCards
						heading={localLang.beegruPremium}
						list_text={localLang.listBeegruPremium}
						icon={BeegruPremium}
					/>
				</CardRow>

				{!isMobile ? (
					<AdditionalCardsRow>
						<AiBoostCard href={"/loading.html?callbackUrl=" + encodeURIComponent("/" + router.locale + "/ai-boost")}>
							<AiBoostContent>
								<AiBoostTitle>Qualified Leads, Faster Closures</AiBoostTitle>
								<AiBoostDescription>
									Launch AI-powered property ads across multiple platforms in minutes. Let qualified leads come to you.
								</AiBoostDescription>
								<AiBoostButton>Try Boost Now</AiBoostButton>
							</AiBoostContent>
							<AiBoostImageContainer>
								<img
									src="/images/icons/ai-boost/rocket-new.png"
									alt="AI Boost Rocket"
									style={{
										width: "100%",
										height: "100%",
										objectFit: "contain",
									}}
								/>
							</AiBoostImageContainer>
						</AiBoostCard>

						<BenefitsCard href={"/loading.html?callbackUrl=" + encodeURIComponent("/" + router.locale + "/broadcast")}>
							<BenefitsCardContent>
								<BenefitsCardTitle>Benefit from the network</BenefitsCardTitle>
								<AiBoostDescription>
									Co-broke with thousands of agents, access exclusive inventory, and grow your business through our
									nationwide network.
								</AiBoostDescription>
								<BroadcastButton>Try Broadcast</BroadcastButton>
							</BenefitsCardContent>
							<AiBoostImageContainer>
								<img
									src="/images/icons/ai-boost/broadcast.png"
									alt="Broadcast Network"
									style={{
										width: "100%",
										height: "100%",
										objectFit: "contain",
									}}
								/>
							</AiBoostImageContainer>
						</BenefitsCard>
					</AdditionalCardsRow>
				) : null}
			</CardsContainer>

			{isMobile ? (
				<MobileOnlySection>
					<AiBoostCard href={"/loading.html?callbackUrl=" + encodeURIComponent("/" + router.locale + "/ai-boost")}>
						<AiBoostContent>
							<AiBoostTitle>Qualified Leads, Faster Closures</AiBoostTitle>
							<AiBoostDescription>
								Launch AI-powered property ads across multiple platforms in minutes. Let qualified leads come to you.
							</AiBoostDescription>
							<AiBoostButton>Try Boost Now</AiBoostButton>
						</AiBoostContent>
						<AiBoostImageContainer>
							<img
								src="/images/icons/ai-boost/rocket-new.png"
								alt="AI Boost Rocket"
								style={{
									width: "100%",
									height: "100%",
									objectFit: "contain",
								}}
							/>
						</AiBoostImageContainer>
					</AiBoostCard>

					<MobileBenefitsDescription>
						<SectionLabel>Additional benefits</SectionLabel>
						<SectionHeading>Why Real Estate Agents Succeed on Beegru</SectionHeading>
						<DescriptionText>
							List inventory, co-broke via Broadcast, and run AI-powered campaigns with AI Boost - all in one
							agent-friendly dashboard. Beegru helps the real estate agent, realtor, and property dealer reach serious
							buyers, keep leads organized, and grow coverage with verified partners in Bangalore and beyond.
						</DescriptionText>
						<DescriptionText>
							From independent property agents to teams, manage statuses, notes, exports, and performance in one view.
							See why real estate agents in Bangalore use Beegru for qualified leads and faster closures.
						</DescriptionText>
					</MobileBenefitsDescription>

					<BenefitsCard href={"/loading.html?callbackUrl=" + encodeURIComponent("/" + router.locale + "/broadcast")}>
						<BenefitsCardContent>
							<BenefitsCardTitle>Benefit from the network</BenefitsCardTitle>
							<AiBoostDescription>
								Co-broke with thousands of agents, access exclusive inventory, and grow your business through our
								nationwide network.
							</AiBoostDescription>
							<BroadcastButton>Try Broadcast Now</BroadcastButton>
						</BenefitsCardContent>
						<AiBoostImageContainer>
							<img
								src="/images/icons/ai-boost/broadcast.png"
								alt="Broadcast Network"
								style={{
									width: "100%",
									height: "100%",
									objectFit: "contain",
								}}
							/>
						</AiBoostImageContainer>
					</BenefitsCard>

					<MobileFAQSection>
						<SectionLabel>Frequently Asked Questions</SectionLabel>
						<FAQContainer>
							<AccordionWrapper>
								{faqs.map((faq, index) => (
									<AccordionItem key={index}>
										<AccordionHeader
											isOpen={openAccordion === index}
											onClick={() => toggleAccordion(index)}
										>
											<AccordionQuestion>{faq.question}</AccordionQuestion>
											<AccordionIcon>{openAccordion === index ? "−" : "+"}</AccordionIcon>
										</AccordionHeader>
										{openAccordion === index && (
											<AccordionContent isOpen={openAccordion === index}>{faq.answer}</AccordionContent>
										)}
									</AccordionItem>
								))}
							</AccordionWrapper>
						</FAQContainer>
					</MobileFAQSection>
				</MobileOnlySection>
			) : (
				<AdditionalBenefitsSection>
					<BenefitsColumn>
						<SectionLabel>Additional benefits</SectionLabel>
						<SectionHeading>Why Real Estate Agents Succeed on Beegru</SectionHeading>
						<DescriptionText>
							List inventory, co-broke via Broadcast, and run AI-powered campaigns with AI Boost - all in one
							agent-friendly dashboard. Beegru helps the real estate agent, realtor, and property dealer reach serious
							buyers, keep leads organized, and grow coverage with verified partners in Bangalore and beyond.
						</DescriptionText>
						<DescriptionText>
							From independent property agents to teams, manage statuses, notes, exports, and performance in one view.
							See why real estate agents in Bangalore use Beegru for qualified leads and faster closures.
						</DescriptionText>
					</BenefitsColumn>
					<BenefitsColumn>
						<SectionLabel>Frequently Asked Questions</SectionLabel>
						<FAQContainer>
							<AccordionWrapper>
								{faqs.map((faq, index) => (
									<AccordionItem key={index}>
										<AccordionHeader
											isOpen={openAccordion === index}
											onClick={() => toggleAccordion(index)}
										>
											<AccordionQuestion>{faq.question}</AccordionQuestion>
											<AccordionIcon>{openAccordion === index ? "−" : "+"}</AccordionIcon>
										</AccordionHeader>
										{openAccordion === index && (
											<AccordionContent isOpen={openAccordion === index}>{faq.answer}</AccordionContent>
										)}
									</AccordionItem>
								))}
							</AccordionWrapper>
						</FAQContainer>
					</BenefitsColumn>
				</AdditionalBenefitsSection>
			)}
		</>
	);
};

export default AgentNewIntro;
